import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { suppliersAPI } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { 
  ArrowLeft, Upload, FileSpreadsheet, CheckCircle2, XCircle, 
  AlertCircle, Download, Loader2, Info
} from 'lucide-react';
import { toast } from 'sonner';

export default function SupplierImportPage() {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const fileInputRef = useRef(null);
  
  const [file, setFile] = useState(null);
  const [skipExisting, setSkipExisting] = useState(true);
  const [importing, setImporting] = useState(false);
  const [results, setResults] = useState(null);
  const [template, setTemplate] = useState(null);

  // Load template info on mount
  React.useEffect(() => {
    loadTemplate();
  }, []);

  const loadTemplate = async () => {
    try {
      const res = await suppliersAPI.importTemplate();
      setTemplate(res.data);
    } catch (err) {
      console.error('Failed to load template info');
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (!selectedFile.name.match(/\.(xlsx|xls)$/)) {
        toast.error('Please select an Excel file (.xlsx or .xls)');
        return;
      }
      setFile(selectedFile);
      setResults(null);
    }
  };

  const handleImport = async () => {
    if (!file) {
      toast.error('Please select a file first');
      return;
    }

    setImporting(true);
    setResults(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('skip_existing', skipExisting);

      const res = await suppliersAPI.importExcel(formData);
      setResults(res.data);
      
      if (res.data.imported > 0) {
        toast.success(`Successfully imported ${res.data.imported} suppliers`);
      } else if (res.data.skipped > 0) {
        toast.info(`All ${res.data.skipped} suppliers already exist`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
      setResults({
        success: false,
        error: err.response?.data?.detail || 'Import failed'
      });
    } finally {
      setImporting(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center text-center">
              <XCircle className="w-12 h-12 text-red-500 mb-4" />
              <h2 className="text-lg font-semibold mb-2">Access Denied</h2>
              <p className="text-slate-500">Only administrators can import suppliers.</p>
              <Button onClick={() => navigate('/suppliers')} className="mt-4">
                Back to Suppliers
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/suppliers')}
              className="text-slate-600"
            >
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <div className="h-4 w-px bg-slate-300" />
            <div className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 text-emerald-600" />
              <h1 className="text-lg font-semibold text-[#0F172A]">Import Suppliers from Excel</h1>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Upload Section */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Upload className="w-5 h-5" />
              Upload Excel File
            </CardTitle>
            <CardDescription>
              Select an Excel file (.xlsx) containing supplier data to import
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* File Input */}
            <div 
              className="border-2 border-dashed border-slate-200 rounded-lg p-8 text-center cursor-pointer hover:border-emerald-400 hover:bg-emerald-50/30 transition-colors"
              onClick={() => fileInputRef.current?.click()}
              data-testid="file-drop-zone"
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileChange}
                className="hidden"
                data-testid="file-input"
              />
              <FileSpreadsheet className="w-12 h-12 mx-auto text-slate-400 mb-3" />
              {file ? (
                <div>
                  <p className="font-medium text-emerald-600">{file.name}</p>
                  <p className="text-sm text-slate-500 mt-1">
                    {(file.size / 1024).toFixed(1)} KB - Click to change file
                  </p>
                </div>
              ) : (
                <div>
                  <p className="font-medium text-slate-700">Click to select Excel file</p>
                  <p className="text-sm text-slate-500 mt-1">or drag and drop here</p>
                </div>
              )}
            </div>

            {/* Options */}
            <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg">
              <Checkbox
                id="skip-existing"
                checked={skipExisting}
                onCheckedChange={setSkipExisting}
              />
              <Label htmlFor="skip-existing" className="text-sm">
                Skip suppliers that already exist (recommended)
              </Label>
            </div>

            {/* Import Button */}
            <Button
              onClick={handleImport}
              disabled={!file || importing}
              className="w-full bg-emerald-600 hover:bg-emerald-700"
              data-testid="import-button"
            >
              {importing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Importing...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Import Suppliers
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Results Section */}
        {results && (
          <Card className={results.success ? 'border-emerald-200' : 'border-red-200'}>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                {results.success ? (
                  <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                ) : (
                  <XCircle className="w-5 h-5 text-red-500" />
                )}
                Import Results
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {results.success ? (
                <>
                  {/* Stats */}
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center p-3 bg-emerald-50 rounded-lg">
                      <p className="text-2xl font-bold text-emerald-600">{results.imported}</p>
                      <p className="text-xs text-emerald-700">Imported</p>
                    </div>
                    <div className="text-center p-3 bg-amber-50 rounded-lg">
                      <p className="text-2xl font-bold text-amber-600">{results.skipped}</p>
                      <p className="text-xs text-amber-700">Skipped</p>
                    </div>
                    <div className="text-center p-3 bg-red-50 rounded-lg">
                      <p className="text-2xl font-bold text-red-600">{results.errors}</p>
                      <p className="text-xs text-red-700">Errors</p>
                    </div>
                  </div>

                  {/* Imported List */}
                  {results.details?.imported_suppliers?.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium mb-2 text-emerald-700">Imported Suppliers:</h4>
                      <div className="max-h-40 overflow-y-auto space-y-1">
                        {results.details.imported_suppliers.map((s, i) => (
                          <div key={i} className="flex items-center gap-2 text-sm bg-emerald-50 p-2 rounded">
                            <CheckCircle2 className="w-3 h-3 text-emerald-500 flex-shrink-0" />
                            <span className="font-mono text-xs text-slate-500">{s.supplier_id}</span>
                            <span className="truncate">{s.name}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Skipped List */}
                  {results.details?.skipped_suppliers?.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium mb-2 text-amber-700">Skipped Suppliers:</h4>
                      <div className="max-h-32 overflow-y-auto space-y-1">
                        {results.details.skipped_suppliers.map((s, i) => (
                          <div key={i} className="flex items-center gap-2 text-sm bg-amber-50 p-2 rounded">
                            <AlertCircle className="w-3 h-3 text-amber-500 flex-shrink-0" />
                            <span className="truncate">{s.name}</span>
                            <span className="text-xs text-slate-500">({s.reason})</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Errors List */}
                  {results.details?.errors?.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium mb-2 text-red-700">Errors:</h4>
                      <div className="max-h-32 overflow-y-auto space-y-1">
                        {results.details.errors.map((e, i) => (
                          <div key={i} className="flex items-center gap-2 text-sm bg-red-50 p-2 rounded">
                            <XCircle className="w-3 h-3 text-red-500 flex-shrink-0" />
                            <span>Row {e.row}: {e.error}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => navigate('/suppliers')}
                  >
                    View Suppliers List
                  </Button>
                </>
              ) : (
                <div className="text-center py-4">
                  <p className="text-red-600">{results.error}</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Template Info */}
        {template && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Info className="w-5 h-5 text-blue-500" />
                Expected Excel Format
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h4 className="text-sm font-medium mb-2">Required Columns:</h4>
                <div className="flex flex-wrap gap-1">
                  {template.expected_columns?.map((col, i) => (
                    <Badge 
                      key={i} 
                      variant={col.includes('Required') ? 'default' : 'secondary'}
                      className="text-xs"
                    >
                      {col}
                    </Badge>
                  ))}
                </div>
              </div>

              <div>
                <h4 className="text-sm font-medium mb-2">Valid Scopes of Approval:</h4>
                <div className="flex flex-wrap gap-1">
                  {template.valid_scopes?.map((scope, i) => (
                    <Badge key={i} variant="outline" className="text-xs">
                      {scope}
                    </Badge>
                  ))}
                </div>
              </div>

              <div>
                <h4 className="text-sm font-medium mb-2">Notes:</h4>
                <ul className="text-sm text-slate-600 space-y-1">
                  {template.notes?.map((note, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="text-emerald-500 mt-1">•</span>
                      {note}
                    </li>
                  ))}
                </ul>
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
